import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Spinner } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getErrorMessage } from '../utils/error';
import { useToast } from '../contexts/ToastContext';

const schema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please provide a valid email address')
    .refine((e) => e.toLowerCase().endsWith('@carion.group'), {
      message: 'Internal user email must end with @carion.group',
    }),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  role: z.literal('internal'),
});

type FormValues = z.infer<typeof schema>;

const RegisterInternal: FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const suffix = import.meta.env.VITE_SAAS_VERS || '';

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', password: '', role: 'internal' },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await api.post('/auth/register', values); // SAME ENDPOINT
      addToast('Registration successful! Please log in.', 'success');
      navigate('/login');
    } catch (err: unknown) {
      addToast(getErrorMessage(err), 'error');
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex justify-center mb-6">
            <img src="/carion.jpg" alt="Carion" className="h-16 object-contain" />
          </div>

          <h2 className="text-3xl font-bold text-center text-gray-900 mb-2">
            Create an Account
          </h2>
          <p className="text-center text-gray-600 mb-8">
            For Account Managers — use your @carion.group email
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <input type="hidden" value="internal" {...register('role')} />

            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                placeholder="John Doe"
                className={`w-full px-4 py-3 border-2 ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                {...register('name')}
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Work Email (@carion.group)
              </label>
              <input
                id="email"
                type="email"
                placeholder="you@carion.group"
                className={`w-full px-4 py-3 border-2 ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                {...register('email')}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                placeholder="Minimum 6 characters"
                className={`w-full px-4 py-3 border-2 ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                {...register('password')}
              />
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-gray-900 to-gray-800 text-white font-semibold rounded-lg hover:from-gray-800 hover:to-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                {isSubmitting ? (<><Spinner size="sm" color="white" /><span>Creating Account...</span></>) : 'Create Internal Account'}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button onClick={() => navigate('/login')} className="text-blue-600 hover:text-blue-700 font-semibold hover:underline cursor-pointer">
                Sign in
              </button>
            </p>
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            Version 0.3 {suffix && `• ${suffix}`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterInternal;
